import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// --- Helper Icon (Updated Styling) ---
const LockIcon = () => (
  <svg className="h-5 w-5 text-slate-400 group-focus-within:text-sky-500 transition-colors" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);
// --- End Helper Icon ---

const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      await resetPassword(token, password, confirmPassword);
      setMessage('Your password has been reset successfully! You can now log in.');
      setTimeout(() => navigate('/login'), 5000);
    } catch (err) {
      setError(err.message || 'Failed to reset password. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Left Pane: Branding */}
      <div className="hidden lg:flex w-1/2 items-center justify-center bg-slate-900 relative overflow-hidden px-12 text-center">
        {/* Abstract Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full z-0 overflow-hidden">
             <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-sky-600 rounded-full blur-[100px] opacity-20"></div>
             <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-blue-600 rounded-full blur-[100px] opacity-20"></div>
        </div>

        <div className="relative z-10 w-full max-w-lg">
          <div className="flex justify-center mb-8">
             <div className="w-24 h-24 bg-sky-600 rounded-2xl flex items-center justify-center text-white font-bold text-5xl shadow-2xl shadow-sky-900/50">
                S
             </div>
          </div>
          <h1 className="text-5xl font-extrabold tracking-tight text-white mb-6">Svinno Retail</h1>
          <p className="text-xl text-slate-300 font-light leading-relaxed">
            Secure your account and get back to business.
          </p>
        </div>
      </div>

      {/* Right Pane: Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-md">
           {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-6">
                 <div className="w-12 h-12 bg-sky-600 rounded-lg flex items-center justify-center text-white font-bold text-2xl shadow-lg">S</div>
          </div>
          
          <h2 className="text-3xl font-bold text-slate-900 text-center lg:text-left">Set a New Password</h2>
          <p className="mt-2 text-center lg:text-left text-sm text-slate-500">
            Enter and confirm your new password below.
          </p>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <LockIcon />
              </div>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="block w-full pl-10 pr-3 py-3 border border-slate-200 rounded-xl leading-5 bg-slate-50 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all sm:text-sm text-slate-900"
                placeholder="New Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading || !!message}
              />
            </div>

            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <LockIcon />
              </div>
              <input
                id="confirm-password"
                name="confirm-password"
                type="password"
                required
                className="block w-full pl-10 pr-3 py-3 border border-slate-200 rounded-xl leading-5 bg-slate-50 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all sm:text-sm text-slate-900"
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading || !!message}
              />
            </div>

            {message && (
                <div className="p-4 bg-green-50 border border-green-100 rounded-xl flex gap-3 text-left">
                    <div className="text-green-500 mt-0.5 shrink-0">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    </div>
                    <div>
                        <p className="text-sm text-green-700 font-medium">{message}</p>
                        <Link to="/login" className="block mt-2 text-sm font-bold text-green-800 hover:text-green-900 underline">
                            Proceed to Login &rarr;
                        </Link>
                    </div>
                </div>
            )}
            
            {error && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-100 flex items-start gap-3">
                    <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    <p className="text-sm text-red-600 font-medium">{error}</p>
                </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading || !!message}
                className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg shadow-sky-500/20 text-sm font-bold text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-70 disabled:cursor-not-allowed transition-all transform active:scale-[0.98]"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Resetting...
                  </>
                ) : 'Reset Password'}
              </button>
            </div>
          </form>

          {!message && (
             <div className="mt-8 text-center text-sm">
                <Link to="/login" className="font-bold text-sky-600 hover:text-sky-500 transition-colors">
                    Back to Login
                </Link>
             </div>
          )}
          
          <div className="mt-12 text-center">
            <p className="text-xs text-slate-400">© {new Date().getFullYear()} Svinno Retail. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;